import { CustomFilter, Form, Input, Modal, Paging, usePaging, Table, EllipsisSpan, Select } from '@maxtropy/components';
import { Cascader } from 'antd';
import { useRequest } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { getDeviceTypeTree } from '@/api/deviceType';
import { getDeviceList, Device } from '@/api/device';
import { formatOptionData } from '@/shared/components/CascadingMultipleSelector/utils';
import { Key } from 'react';
import { DeviceItem } from '../../types';
import { apiV2ComprehensiveLabelListPost } from '@maxtropy/device-customer-apis-v2';

interface DeviceSelectModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (selectedDevices: DeviceItem[]) => void;
  /** 弹窗标题 */
  title?: string;
  /** 是否展示设备标签筛选 */
  showTagFilter?: boolean;
  /** 需要禁用的设备（不可取消） */
  disabledDevices?: DeviceItem[];
  /** 默认已选设备 */
  selectedDevices?: DeviceItem[];
}

// 表格列
const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v?: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const toDeviceItem = (device?: Device | DeviceItem): DeviceItem | undefined => {
  if (!device?.id) {
    return undefined;
  }

  return {
    id: device.id,
    name: device.name,
  };
};

const DeviceSelectModal: React.FC<DeviceSelectModalProps> = ({
  visible,
  onCancel,
  onOk,
  title = '选择设备',
  showTagFilter = false,
  disabledDevices,
  selectedDevices,
}) => {
  const [form] = Form.useForm();
  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<DeviceItem[]>([]);
  const [searchParams, setSearchParams] = useState<{
    name?: string;
    deviceTypeIdList?: number[][];
    labelIds?: number[];
  }>({});

  // 获取设备类型树
  const { data: deviceTypeData } = useRequest(() => getDeviceTypeTree());
  const { data: labelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }), {
    ready: showTagFilter,
  });

  // 格式化设备类型选项
  const deviceTypeOptions = useMemo(() => {
    return formatOptionData(deviceTypeData);
  }, [deviceTypeData]);

  // 已绑定到当前介质下的设备需要显示为默认选中，同时禁止取消勾选。
  const disabledDeviceIds = useMemo(() => (disabledDevices ?? []).map(device => device.id), [disabledDevices]);
  const defaultSelectedDevices = useMemo(
    () => selectedDevices ?? disabledDevices ?? [],
    [selectedDevices, disabledDevices]
  );

  useEffect(() => {
    if (visible) {
      // 弹窗每次重新打开都回到初始状态，避免沿用上一次查询条件。
      form.resetFields();
      setPageOffset(1);
      setSearchParams({});
      setSelectedRowKeys(defaultSelectedDevices.map(item => item.id));
      setSelectedRows(defaultSelectedDevices);
    }
  }, [defaultSelectedDevices, form, setPageOffset, visible]);

  // 获取设备列表
  const { data: deviceList, loading } = useRequest(
    async () => {
      const typeIds = searchParams.deviceTypeIdList?.flat();
      const res = await getDeviceList({
        codeOrName: searchParams.name,
        typeId: typeIds,
        labelIds: searchParams.labelIds,
        page: pageOffset,
        size: pageSize,
        invalid: false,
        filterEdgeDevice: true,
      });
      setTotalCount(res?.total ?? 0);
      return res;
    },
    {
      refreshDeps: [pageOffset, pageSize, searchParams],
    }
  );

  // 搜索
  const handleSearch = (values: any) => {
    setSearchParams({
      name: values.name,
      deviceTypeIdList: values.deviceTypeIdList,
      labelIds: values.labelIds ? [values.labelIds] : undefined,
    });
    setPageOffset(1);
  };

  // 重置
  const handleReset = () => {
    form.resetFields();
    setSearchParams({});
    setPageOffset(1);
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (keys: Key[], rows: Device[]) => {
      setSelectedRowKeys(keys);
      const currentPageDevices = deviceList?.list || [];
      const currentPageDeviceIds = new Set(currentPageDevices.map(device => device.id));

      setSelectedRows(prev => {
        // 翻页时表格只返回当前页 rows，这里先保留其他页已选中的设备，
        // 同时保留当前页里那些“默认选中且禁用”的设备。
        const preservedDevices = prev.filter(device => {
          if (!currentPageDeviceIds.has(device.id)) {
            return true;
          }

          return disabledDeviceIds.includes(device.id);
        });

        const currentSelectedDevices = rows.map(toDeviceItem).filter((device): device is DeviceItem => !!device);

        // 当前页最新勾选结果与历史已选结果合并后，按 id 去重得到最终提交数据。
        return Array.from(
          new Map([...preservedDevices, ...currentSelectedDevices].map(device => [device.id, device])).values()
        );
      });
    },
    getCheckboxProps: (record: Device) => ({
      disabled: disabledDeviceIds.includes(record.id),
    }),
  };

  return (
    <Modal
      title={title}
      open={visible}
      destroyOnClose
      onCancel={onCancel}
      onOk={() => onOk(selectedRows)}
      bodyScroll={true}
      size="big"
    >
      <CustomFilter form={form} onFinish={handleSearch} colSpan={8} onReset={handleReset}>
        <Form.Item name="name" label="编号/名称">
          <Input placeholder="请输入编号或名称查询" allowClear />
        </Form.Item>
        <Form.Item name="deviceTypeIdList" label="所属类目">
          <Cascader
            options={deviceTypeOptions}
            fieldNames={{ children: 'child' }}
            multiple
            maxTagCount="responsive"
            placeholder="请选择所属类目"
            showSearch
            allowClear
            style={{ minWidth: 200 }}
          />
        </Form.Item>
        {showTagFilter && (
          <Form.Item name="labelIds" label="设备标签">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={(labelList?.list ?? []).map(item => ({ label: item.name, value: item.id }))}
              placeholder="请选择设备标签"
            />
          </Form.Item>
        )}
      </CustomFilter>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={deviceList?.list || []}
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ y: 340 }}
        showSelectedCount
        selectedCount={selectedRowKeys?.length ?? 0}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default DeviceSelectModal;
